import React, { useEffect, useMemo, useRef, useState } from 'react';
import { amActions } from '../amStore';
import { dayLabel, monthLabel } from '../amRules';
import { Panel, fmtInt, fmtNum } from '../amUi';

const n0 = (v) => (v === null || v === undefined ? '' : v);
const COLUMNS = [
  { key: 'lr', label: 'LR No', on: true },
  { key: 'inv', label: 'Invoice No', on: true },
  { key: 'veh', label: 'Vehicle No', on: true },
  { key: 'tripId', label: 'Trip ID' },
  { key: 'dispatch', label: 'Dispatch date', on: true, fmt: dayLabel },
  { key: 'month', label: 'Dispatch month', fmt: monthLabel },
  { key: 'delivery', label: 'Delivery date', on: true, fmt: dayLabel },
  { key: 'status', label: 'Status', on: true },
  { key: 'type', label: 'Shipment type', on: true },
  { key: 'invoiceType', label: 'Invoice type' },
  { key: 'entity', label: 'Entity', on: true },
  { key: 'transporter', label: 'Transporter', on: true },
  { key: 'tptCat', label: 'Transport category' },
  { key: 'org', label: 'Organised / unorganised' },
  { key: 'srcCity', label: 'Origin', on: true },
  { key: 'srcPin', label: 'Source pincode' },
  { key: 'dstCity', label: 'Destination city', on: true },
  { key: 'dstPin', label: 'Destination pincode' },
  { key: 'dstState', label: 'Destination state' },
  { key: 'dstZone', label: 'Destination zone' },
  { key: 'lane', label: 'Lane' },
  { key: 'primSec', label: 'Primary / secondary' },
  { key: 'vehicle', label: 'Vehicle type' },
  { key: 'weight', label: 'Weight (kg)', num: true, fmt: (v) => (v === null ? '' : fmtInt(v)) },
  { key: 'weightSource', label: 'Weight source' },
  { key: 'cbm', label: 'CBM', num: true, fmt: (v) => (v === null ? '' : fmtNum(v, 2)) },
  { key: 'eta', label: 'Expected ETA', fmt: dayLabel },
  { key: 'cpd', label: 'Customer promise date', fmt: dayLabel },
  { key: 'edd', label: 'Estimated delivery date', fmt: dayLabel },
  { key: 'perf', label: 'Performance' },
  { key: 'delayDays', label: 'Delay days', num: true },
  { key: 'remarkCat', label: 'Remark category' },
  { key: 'newPerf', label: 'New performance', on: true, get: (r) => (r.delivered ? r.newPerf : '') },
  { key: 'exception', label: 'Exception reason', on: true, get: (r) => (r.excused ? r.remarkCat : '') },
  { key: 'deliveryDays', label: 'Delivery days', num: true, on: true },
  { key: 'breachDays', label: 'Breach days', num: true },
  { key: 'pod', label: 'POD status', on: true },
  { key: 'podDate', label: 'POD received date', fmt: dayLabel },
  { key: 'podBucket', label: 'POD ageing bucket' },
  { key: 'distKm', label: 'Distance (km)', num: true, fmt: (v) => (v === null ? 'Needs distance' : fmtInt(v)) },
  { key: 'distSource', label: 'Distance source' },
  { key: 'co2', label: 'CO₂ (t)', num: true, fmt: (v) => (v === null ? '' : fmtNum(v, 3)) },
];
const PAGE_SIZES = [25, 50, 100];

const raw = (c, r) => (c.get ? c.get(r) : r[c.key]);
const show = (c, r) => { const v = raw(c, r); return c.fmt ? c.fmt(v) : n0(v); };

function DqTiles({ dq }) {
  if (!dq) return null;
  const tiles = [
    ['Records in extract', dq.raw, 'After the All-tab eligibility (cancelled excluded)'],
    ['Dispatched outside the date range', dq.outsideRange, 'Returned by the API, not analysed'],
    ['“Document Purpose” LRs removed', dq.documentPurpose],
    ['Exact duplicates removed', dq.duplicates, 'LR + invoice + transporter'],
    ['Records analysed', dq.analysed, null, true],
    ['Delivery date before dispatch', dq.deliveryBeforeDispatch],
    ['Delivered with no delivery date', dq.deliveredNoDate],
    ['Missing destination state / zone', dq.missingDestStateZone],
    ['Primary shipments', dq.primary, 'Destination is a source pincode'],
    ['…of which to own source pincode', dq.primaryOwnPin],
    ['CBM above 100 treated as invalid', dq.cbmOver100],
    ['Rows with no mappable distance', dq.noDistance],
    ['Vehicle types without capacity', dq.vehicleTypesNoCapacity.length, dq.vehicleTypesNoCapacity.join(', ') || 'None'],
    ['Missing expected ETA', dq.missingEta],
    ['Missing customer promise date', dq.missingCpd],
    ['Missing estimated delivery date', dq.missingEdd],
    ['Blank vehicle type', dq.blankVehicleType],
    ['Zero or blank weight', dq.zeroWeight],
    ['FTL weight from invoice lines (t)', dq.ftlWeightInvoiceKg / 1000, `Trip records alone: ${fmtInt(dq.ftlWeightTripKg / 1000)} t`],
    ['FTL records with no invoice lines', dq.ftlNoInvoiceLines, 'Trip-record weight used'],
    ['FTL records under 50 kg', dq.ftlTinyWeight, 'Possibly entered in tonnes'],
    ['Delivered, blank performance', dq.blankPerformance, `${fmtInt(dq.blankPerformanceDelayZero)} have delay days = 0`],
    ['Delivered, POD never submitted', dq.podBlank, 'is_submitted_pod missing'],
    ['POD date before delivery date', dq.podDateBeforeDelivery],
    ['POD received with no date', dq.podReceivedNoDate],
  ];
  return (
    <Panel title="Data quality" subtitle="Run on every refresh, before any filter · blanks are reported, never filled">
      <div className="am-dq">
        {tiles.map(([label, value, note, hl]) => (
          <div key={label} className={`am-dq-tile ${hl ? 'hl' : ''} ${!hl && value > 0 ? 'warn' : ''}`}>
            <b>{fmtInt(value)}</b><span>{label}</span>{note && <small title={note}>{note}</small>}
          </div>
        ))}
      </div>
    </Panel>
  );
}

function toCsv(rows, cols) {
  const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [cols.map((c) => esc(c.label)).join(',')];
  rows.forEach((r) => lines.push(cols.map((c) => esc(c.num ? n0(raw(c, r)) : show(c, r))).join(',')));
  return lines.join('\r\n');
}

export default function ExplorerPage({ ctx }) {
  const { s, rows } = ctx;
  const [visible, setVisible] = useState(() => COLUMNS.filter((c) => c.on).map((c) => c.key));
  const [sort, setSort] = useState({ key: 'dispatch', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [chooser, setChooser] = useState(false);
  const chooserRef = useRef(null);
  useEffect(() => { setPage(0); }, [rows, size]);
  useEffect(() => {
    if (!s.explorerSort) return;
    setSort({ key: s.explorerSort, dir: 'desc' });
    amActions.setExplorerSort(null);
  }, [s.explorerSort]);
  useEffect(() => {
    if (!chooser) return undefined;
    const close = (e) => { if (chooserRef.current && !chooserRef.current.contains(e.target)) setChooser(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [chooser]);

  const cols = COLUMNS.filter((c) => visible.includes(c.key));
  const sorted = useMemo(() => {
    const c = COLUMNS.find((x) => x.key === sort.key);
    if (!c) return rows;
    return [...rows].sort((a, b) => {
      const x = raw(c, a); const y = raw(c, b);
      if (x === y) return 0;
      if (x === null || x === undefined || x === '') return 1;
      if (y === null || y === undefined || y === '') return -1;
      const cmp = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / size));
  const slice = sorted.slice(page * size, page * size + size);

  const exportCsv = () => {
    const blob = new Blob([`﻿${toCsv(sorted, cols)}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `autometrics_shipments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <DqTiles dq={s.dq} />
      <Panel title="Shipment explorer" subtitle={`${fmtInt(rows.length)} LR records under the current filters · reconciles with Total shipments on every page`}
        actions={(
          <>
            <div className="am-chooser" ref={chooserRef}>
              <button type="button" className="am-btn ghost sm" onClick={() => setChooser((v) => !v)}>Columns ({cols.length})</button>
              {chooser && (
                <div className="am-ms-pop right">
                  <div className="am-ms-actions">
                    <button type="button" onClick={() => setVisible(COLUMNS.map((c) => c.key))}>All</button>
                    <button type="button" onClick={() => setVisible(COLUMNS.filter((c) => c.on).map((c) => c.key))}>Default</button>
                  </div>
                  <div className="am-ms-list">
                    {COLUMNS.map((c) => (
                      <label key={c.key}>
                        <input type="checkbox" checked={visible.includes(c.key)}
                          onChange={() => setVisible((v) => (v.includes(c.key) ? v.filter((k) => k !== c.key) : [...v, c.key]))} />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" className="am-btn primary sm" onClick={exportCsv} disabled={!rows.length}>Export CSV ({fmtInt(rows.length)})</button>
          </>
        )}>
        <div className="am-table-wrap scroll-x">
          <table className="am-table am-explorer">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className={`${c.num ? 'r' : ''} ${sort.key === c.key ? 'sorted' : ''}`}
                    onClick={() => setSort((x) => ({ key: c.key, dir: x.key === c.key && x.dir === 'desc' ? 'asc' : 'desc' }))}>
                    {c.label}{sort.key === c.key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slice.length === 0 && <tr><td colSpan={cols.length} className="am-empty">No shipments match the current filters.</td></tr>}
              {slice.map((r) => (
                <tr key={r.id}>
                  {cols.map((c) => {
                    const v = show(c, r);
                    let cls = c.num ? 'r' : '';
                    if (c.key === 'newPerf') cls += v === 'Delay' ? ' bad' : v === 'On Time' ? ' good' : '';
                    if (c.key === 'lr' || c.key === 'veh') {
                      return <td key={c.key}><button type="button" className="am-link" title="Filter every page to this value" onClick={() => amActions.setSearch(String(raw(c, r)))}>{v}</button></td>;
                    }
                    return <td key={c.key} className={cls}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="am-pager">
          <span>Rows per page
            <select className="am-select" value={size} onChange={(e) => setSize(Number(e.target.value))}>{PAGE_SIZES.map((p) => <option key={p} value={p}>{p}</option>)}</select>
          </span>
          <span>{fmtInt(rows.length ? page * size + 1 : 0)}–{fmtInt(Math.min(rows.length, (page + 1) * size))} of {fmtInt(rows.length)}</span>
          <button type="button" className="am-btn ghost sm" disabled={page === 0} onClick={() => setPage(0)}>«</button>
          <button type="button" className="am-btn ghost sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
          <span>Page {page + 1} of {fmtInt(pages)}</span>
          <button type="button" className="am-btn ghost sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
          <button type="button" className="am-btn ghost sm" disabled={page >= pages - 1} onClick={() => setPage(pages - 1)}>»</button>
        </div>
      </Panel>
    </>
  );
}
